/**
 * Copyright (c) 2025 Robert Schiwon
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of
 * this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to
 * use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
 * the Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
 * FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
 * IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
 * CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
import { isSameUrl } from 'util/UrlUtils';
import CustomEventsMessageBus from 'util/CustomEventsMessageBus';
import './DataData.css';
const template = `
  <textarea readonly></textarea>
`;
const nodeSelectedEvent = 'sf-tree-node-selected';
const nodeDeselectedEvent = 'sf-tree-node-deselected';
const nodeDataUpdatedEvent = 'sf-tree-node-data-updated';
export default class DataData extends HTMLElement {
    #initialized = false;
    #channel = CustomEventsMessageBus.getDefaultChannel();
    #handles = [];
    #url = null;
    #data = [];
    get data() {
        if (this.#data === null) {
            return [];
        }
        return this.#data;
    }
    set data(data) {
        this.#data = data;
        this.render();
    }
    init() {
        if (!this.#initialized) {
            this.innerHTML = template;
            this.#initialized = true;
        }
    }
    render() {
        const textarea = this.querySelector('textarea');
        let text = '';
        for (const data of this.data) {
            const x = String(data.x).padEnd(25);
            const y = String(data.y);
            const row = `${x}; ${y}\n`;
            text += row;
        }
        textarea.value = text;
    }
    handleDataChanged(message) {
        const url = new URL(message.detail.url);
        const sameUrl = isSameUrl(this.#url, url);
        if (!sameUrl && message.name === nodeSelectedEvent) {
            this.#url = url;
            this.data = message.detail.data;
        }
        else if (sameUrl && message.name === nodeDeselectedEvent) {
            this.#url = null;
            this.data = [];
        }
        else if (sameUrl && message.name === nodeDataUpdatedEvent) {
            this.data = message.detail.data;
        }
    }
    connectedCallback() {
        this.init();
        const handle0 = this.#channel.addListener(nodeSelectedEvent, this.handleDataChanged.bind(this));
        const handle1 = this.#channel.addListener(nodeDeselectedEvent, this.handleDataChanged.bind(this));
        const handle2 = this.#channel.addListener(nodeDataUpdatedEvent, this.handleDataChanged.bind(this));
        this.#handles.push(handle0, handle1, handle2);
        this.render();
    }
    disconnectedCallback() {
        for (const handle of this.#handles) {
            this.#channel.removeListener(handle);
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    attributeChangedCallback(name, oldValue, newValue) {
        this.init();
    }
}
customElements.define('sf-data-data', DataData);
